import {
  Badge, Button, CONSENT_TYPES, Card, ErrorState, Field, Input, LoadingState, Notice, Permissions, Row, Select,
  Stack, useApi, useI18n, usePermission, type ConsentResponse, type ConsentType,
} from '@daghfal/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

type Standing = 'granted' | 'refused' | 'unanswered';

/**
 * The family's four consents, recorded by the administration from the signed form (Q23, Q45).
 *
 * Guardians record nothing. Without individual-photos consent the child is not photographed or tagged at all,
 * and withdrawing a photo consent pulls the pictures it no longer allows out of every gallery and chat, which
 * the card reports with the count the API returns.
 */
export function ConsentsCard({ childId }: { childId: number }) {
  const api = useApi();
  const { t, tn, formatDate } = useI18n();
  const queryClient = useQueryClient();
  const canUpdate = usePermission(Permissions.children.update);

  const [evidenceRef, setEvidenceRef] = useState('');
  const [onBehalfOf, setOnBehalfOf] = useState<number | ''>('');
  const [evidenceError, setEvidenceError] = useState<string | undefined>();
  const [withdrawn, setWithdrawn] = useState<number | null>(null);

  const key = ['consents', childId];
  const consents = useQuery({ queryKey: key, queryFn: () => api.consents.list(childId) });
  const guardians = useQuery({
    queryKey: ['guardians', childId],
    queryFn: () => api.guardians.forChild(childId),
    enabled: canUpdate,
  });

  const record = useMutation({
    mutationFn: ({ type, granted }: { type: ConsentType; granted: boolean }) => api.consents.record(childId, {
      consentType: type,
      granted,
      evidenceRef: evidenceRef.trim() || null,
      guardianMembershipId: onBehalfOf === '' ? null : onBehalfOf,
    }),
    onSuccess: (result) => {
      setWithdrawn(result.granted ? null : result.withdrawnPhotos);
      void queryClient.invalidateQueries({ queryKey: key });
      void queryClient.invalidateQueries({ queryKey: ['children'] });
      void queryClient.invalidateQueries({ queryKey: ['consent-gaps'] });
    },
  });

  // A grant is recorded from the signed form, so its reference is asked for here rather than refused later.
  const submit = (type: ConsentType, granted: boolean) => {
    if (granted && !evidenceRef.trim()) {
      setEvidenceError(t('consent.evidence.required'));
      return;
    }
    setEvidenceError(undefined);
    record.mutate({ type, granted });
  };

  return (
    <Card title={t('consent.title')} hint={t('consent.hint')}>
      <Stack>
        {consents.isPending && <LoadingState />}
        {consents.isError && <ErrorState error={consents.error} onRetry={() => void consents.refetch()} />}
        {record.isError && <ErrorState error={record.error} />}
        {withdrawn !== null && <Notice tone="info">{tn('consent.withdrawnPhotos', withdrawn)}</Notice>}

        {canUpdate && (
          <Row>
            <Field label={t('consent.evidence')} hint={t('consent.evidence.hint')} error={evidenceError}>
              {(props) => <Input {...props} value={evidenceRef} onChange={(event) => setEvidenceRef(event.target.value)} />}
            </Field>
            <Field label={t('consent.onBehalfOf')}>
              {(props) => (
                <Select
                  {...props}
                  value={onBehalfOf}
                  onChange={(event) => setOnBehalfOf(event.target.value === '' ? '' : Number(event.target.value))}
                >
                  <option value="">{t('consent.onBehalfOf.none')}</option>
                  {(guardians.data ?? []).map((guardian) => (
                    <option key={guardian.guardianMembershipId} value={guardian.guardianMembershipId}>
                      {guardian.fullName ?? guardian.phone}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </Row>
        )}

        {consents.data && CONSENT_TYPES.map((type) => {
          const latest = latestOf(consents.data, type);
          const standing = standingOf(consents.data, type);
          const busy = record.isPending && record.variables?.type === type;

          return (
            <Row key={type} between>
              <span className="dg-stack dg-stack--tight">
                <strong>{t(`consent.${type}`)}</strong>
                {latest && (
                  <span className="dg-row">
                    <span className="dg-card__hint">
                      {t('consent.recordedBy', {
                        name: latest.recordedByName ?? '—',
                        date: formatDate(latest.grantedAt ?? latest.revokedAt ?? ''),
                      })}
                    </span>
                    {latest.guardianName && (
                      <span className="dg-card__hint">{t('consent.onBehalfOfName', { name: latest.guardianName })}</span>
                    )}
                    {latest.evidenceRef && <span className="dg-card__hint">{latest.evidenceRef}</span>}
                  </span>
                )}
              </span>
              <span className="dg-row">
                <Badge tone={standing === 'granted' ? 'success' : standing === 'refused' ? 'danger' : 'warning'}>
                  {standing === 'granted'
                    ? t('consent.granted')
                    : standing === 'refused' ? t('consent.notGranted') : t('consent.unanswered')}
                </Badge>
                {canUpdate && standing === 'granted' && (
                  <Button variant="ghost" busy={busy} onClick={() => submit(type, false)}>{t('consent.revoke')}</Button>
                )}
                {canUpdate && standing !== 'granted' && (
                  <Button variant="secondary" busy={busy} onClick={() => submit(type, true)}>{t('consent.grant')}</Button>
                )}
                {canUpdate && standing === 'unanswered' && (
                  <Button variant="ghost" busy={busy} onClick={() => submit(type, false)}>{t('consent.refuse')}</Button>
                )}
              </span>
            </Row>
          );
        })}
      </Stack>
    </Card>
  );
}

/** Granted while one record of the type stands; refused once any answer exists; otherwise unanswered. */
function standingOf(consents: ConsentResponse[], type: ConsentType): Standing {
  const ofType = consents.filter((consent) => consent.consentType === type);
  if (ofType.some((consent) => consent.isCurrent)) return 'granted';
  return ofType.length > 0 ? 'refused' : 'unanswered';
}

/** The most recent answer of the type: a grant is dated when given, a refusal or withdrawal when made. */
function latestOf(consents: ConsentResponse[], type: ConsentType): ConsentResponse | undefined {
  const at = (consent: ConsentResponse) => consent.revokedAt && !consent.granted
    ? consent.revokedAt
    : consent.grantedAt ?? consent.revokedAt ?? '';
  return consents
    .filter((consent) => consent.consentType === type)
    .sort((a, b) => at(b).localeCompare(at(a)))[0];
}
